import { Howl } from 'howler';
import { Debug, LogCategory, LogLevel } from '../../../shared/Debug';

/**
 * Manages sound effects for the game with graceful fallbacks
 */
export class SoundManager {
  private static instance: SoundManager;
  private sounds: Map<string, Howl> = new Map();
  private isMuted: boolean = false;

  // Sound effect paths
  public readonly SOUND_PATHS = {
    bounce: '/assets/sounds/bounce.mp3',
    swish: '/assets/sounds/swish.mp3',
    whistle: '/assets/sounds/whistle.mp3',
    pass: '/assets/sounds/pass.mp3',
    crowd: '/assets/sounds/crowd.mp3',
    celebration: '/assets/sounds/celebration.mp3',
    cheer1: '/assets/sounds/cheer1.mp3',
    cheer2: '/assets/sounds/cheer2.mp3',
    cheer3: '/assets/sounds/cheer3.mp3',
    cheer4: '/assets/sounds/cheer4.mp3',
    cheer5: '/assets/sounds/cheer5.mp3',
    
  };

  private constructor() {
    this.loadSounds();
  }

  public static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  private async loadSounds(): Promise<void> {
    for (const [key, path] of Object.entries(this.SOUND_PATHS)) {
      try {
        const sound = new Howl({
          src: [path],
          html5: true,
          // Special handling for crowd sound to make it loop seamlessly
          ...(key === 'crowd' ? {
            sprite: {
              ambient: [0, 9900, true] // Slightly longer duration to ensure no gap
            },
            loop: false, // We'll use sprite looping instead
            onloaderror: () => {
              Debug.warn(`Failed to load crowd sound: ${path}`, LogCategory.GENERAL);
            },
            onplayerror: () => {
              Debug.warn('Crowd sound playback error', LogCategory.GENERAL);
            },
            onend: () => {
              Debug.info('Crowd sound sprite ended, should loop automatically', LogCategory.GENERAL);
            },
            onload: () => {
              Debug.info('Crowd sound loaded successfully', LogCategory.GENERAL);
            }
          } : {})
        });
        this.sounds.set(key, sound);
      } catch (error) {
        Debug.warn(`Could not load sound effect: ${key} (${path})`, LogCategory.GENERAL);
      }
    }
  }

  public play(soundName: keyof typeof this.SOUND_PATHS, volume: number = 1.0): void {
    if (this.isMuted) return;

    const sound = this.sounds.get(soundName);
    if (sound) {
      sound.volume(volume);
      if (soundName === 'crowd') {
        sound.play('ambient');
      } else {
        sound.play();
      }
    } else {
      Debug.warn(`Attempted to play missing sound effect: ${soundName}`, LogCategory.GENERAL);
    }
  }

  public playLoop(soundName: keyof typeof this.SOUND_PATHS, volume: number = 1.0): void {
    if (this.isMuted) return;

    const sound = this.sounds.get(soundName);
    if (sound) {
      sound.volume(volume);
      if (soundName === 'crowd') {
        if (!sound.playing()) {
          sound.play('ambient');
        }
      } else if (!sound.playing()) {
        sound.play();
      }
    } else {
      Debug.warn(`Attempted to play missing sound effect: ${soundName}`, LogCategory.GENERAL);
    }
  }

  public toggleMute(): void {
    this.isMuted = !this.isMuted;
    if (this.isMuted) {
      this.sounds.forEach(sound => sound.mute(true));
    } else {
      this.sounds.forEach(sound => sound.mute(false));
    }
  }

  public setMute(muted: boolean): void {
    this.isMuted = muted;
    this.sounds.forEach(sound => sound.mute(muted));
  }

  public stopAll(): void {
    this.sounds.forEach(sound => sound.stop());
  }

  public stopLoop(soundName: keyof typeof this.SOUND_PATHS): void {
    const sound = this.sounds.get(soundName);
    if (sound) {
      sound.stop();
    }
  }
}

// Export a singleton instance
export const soundManager = SoundManager.getInstance(); 